import * as React from 'react';
import { Button } from '@/hen-ui/atomics/Button';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Image } from '@/hen-ui/atomics/Image';
import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { CardContent } from '@/hen-ui/composites/Card';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { Rating } from '@/hen-ui/composites/Rating';
import {
  studentLoansInlineCardClasses,
  studentLoansInlineCardContentClasses,
  studentLoansInlineContentClasses,
  studentLoansInlineCopyClasses,
  studentLoansInlineDisclosureClasses,
  studentLoansInlinePartnerClasses,
  studentLoansInlineRootClasses,
} from './student-loans-inline.layout';
import type { StudentLoansInlineProps } from './types';

export const StudentLoansInline = React.forwardRef<
  HTMLDivElement,
  StudentLoansInlineProps
>(
  (
    {
      variant = 'slim',
      title,
      subtitle,
      partner,
      cta,
      disclosure,
      onCtaClick,
      ...props
    },
    ref,
  ) => {
    return (
      <CardRoot
        ref={ref}
        variant={
          variant === 'slim' ? 'background-gray' : 'background-transparent'
        }
        border={variant === 'slim' ? 'strokeLight' : 'none'}
        padding='none'
        rounded='lg'
        className={studentLoansInlineCardClasses(variant)}
        role='region'
        aria-label={title}
        data-student-loans-inline-variant={variant}
        {...props}
        data-hen-id={COMPONENT_IDS.StudentLoansInline}
      >
        <CardContent
          contentPadding='none'
          className={studentLoansInlineCardContentClasses(variant)}
        >
          <div className={studentLoansInlineRootClasses(variant)}>
            <div className={studentLoansInlineContentClasses(variant)}>
              <Flex
                direction='column'
                className={studentLoansInlinePartnerClasses(variant)}
              >
                <Text variant='l2' weight='bold' color='subtext' tag='span'>
                  {partner.label}
                </Text>
                <Image
                  src={partner.logo}
                  alt={partner.name}
                  className='h-6 w-auto object-contain object-left'
                />
                {partner.showRating !== false && (
                  <Rating
                    rating={partner.rating}
                    ratingCount={partner.ratingCount}
                    showRatingCount
                    showRatingTooltip={partner.showRatingTooltip}
                    ratingTooltip={partner.ratingTooltip}
                    tooltipSide='right'
                  />
                )}
                {variant === 'slim' && (
                  <Text
                    variant='l2'
                    color='default'
                    className={studentLoansInlineDisclosureClasses(variant)}
                    data-student-loans-inline-disclosure=''
                  >
                    {disclosure}
                  </Text>
                )}
              </Flex>

              <Flex
                direction='column'
                gap='min'
                className={studentLoansInlineCopyClasses(variant)}
              >
                <div className='flex min-w-0 flex-col items-start gap-min md:flex-row md:items-center md:justify-between md:gap-lg'>
                  <Flex
                    direction='column'
                    gap='min'
                    className='min-w-0 flex-1 overflow-hidden'
                  >
                    <Heading variant={variant === 'slim' ? 'h4' : 'h5'}>
                      {title}
                    </Heading>
                    <Text variant='b5' color='default'>
                      {subtitle}
                    </Text>
                  </Flex>
                  <Button
                    asChild
                    variant='primaryFill'
                    size='sm'
                    className='shrink-0'
                  >
                    <a
                      href={cta.href}
                      target='_blank'
                      rel='noopener noreferrer sponsored'
                      onClick={onCtaClick}
                    >
                      {cta.label}
                    </a>
                  </Button>
                </div>
              </Flex>
            </div>

            {variant === 'narrow' && (
              <Text
                variant='l2'
                color='default'
                className={studentLoansInlineDisclosureClasses(variant)}
                data-student-loans-inline-disclosure=''
              >
                {disclosure}
              </Text>
            )}
          </div>
        </CardContent>
      </CardRoot>
    );
  },
);

StudentLoansInline.displayName = 'StudentLoansInline';
